// TODO @consumer: Move to global components

import React, { Fragment, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { getAsset } from '../../../util/assets';
import Button from '../Button';
import withModal from '../modal/withModal';
import { ALERT_MODAL } from '../../../constants/modals';
import WarningIcon from '../../icons/WarningIcon';

const propTypes = {
  onRequestClose: PropTypes.func.isRequired,
  actionProps: PropTypes.shape({
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    message: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.node,
      PropTypes.func,
    ]),
    info: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    type: PropTypes.oneOf(['error', 'info', 'success', 'note']),
    onDismiss: PropTypes.func,
    dismissible: PropTypes.bool,
  }),
};

const AlertModal = ({ onRequestClose, actionProps }) => {
  // Default to error here since this was originally an error modal and we don't want
  // to break all instances of it.
  const { type = 'error' } = actionProps || {};

  const alertTypeDefaults = (() => {
    switch (type) {
      case 'error': {
        return {
          title: 'Sorry, something went wrong',
          asset: '/waldo-icon-error.svg',
          alt: 'Confused Waldo',
          message:
            'I could not process your request this time, please try again in a while.',
        };
      }
      case 'success':
        return {
          title: 'All good!',
          asset: '/waldo-excited.png',
          alt: 'Waldo',
          message: 'Looks like everything went according to the plan.',
        };
      case 'note':
        return {
          title: 'Note',
          asset: <WarningIcon color="#9bccfe" size={24} />,
          alt: '',
          message: '',
        };
      case 'info':
      default:
        return {
          title: '',
          asset: '',
          alt: '',
          message: '',
        };
    }
  })();

  const {
    message,
    info,
    title,
    asset,
    alt,
    btnLabel,
    onDismiss,
    dismissible = true,
    contentClassName,
    timeout,
  } = {
    // Default for all types:
    btnLabel: 'Dismiss',
    ...alertTypeDefaults,
    ...actionProps,
  };

  const renderMessage = () => {
    if (React.isValidElement(message)) {
      return message;
    }

    if (typeof message === 'function') {
      return message({ onDismiss, onRequestClose });
    }

    return message && <p>{message}</p>;
  };

  const contentClasses = classNames({
    'alert-modal-content': true,
    [contentClassName]: !!contentClassName,
  });

  useEffect(() => {
    let timeoutItemId;
    if (timeout) {
      timeoutItemId = setTimeout(onRequestClose, timeout);
    }

    return () => {
      if (timeoutItemId) {
        clearTimeout(timeoutItemId);
      }
    };
  }, [timeout]);

  return (
    <Fragment>
      <div className={contentClasses}>
        {asset && (
          <>
            {React.isValidElement(asset) ? (
              asset
            ) : (
              <img
                src={getAsset(asset)}
                alt={alt}
                width="54px"
                className="mb-2"
              />
            )}
          </>
        )}
        {title && <p className="text-extra-bold mb-0">{title}</p>}

        {renderMessage()}

        {info &&
          (React.isValidElement(info) ? (
            info
          ) : (
            <p className="alert-modal-content-info">{info}</p>
          ))}
      </div>

      {dismissible && (
        <Button
          onClick={() => {
            onDismiss?.();
            onRequestClose();
          }}
          btnBlock
          btnStyle="link"
          className="btn-alert-modal-dismiss py-2"
        >
          {btnLabel}
        </Button>
      )}
    </Fragment>
  );
};

AlertModal.propTypes = propTypes;

export default withModal(AlertModal, {
  name: ALERT_MODAL,
  hideHeader: true,
  size: 'xs',
  className: 'alert-modal',
  overlayClassName: 'alert-modal-overlay',
  shouldCloseOnOverlayClick: false,
});
